/**
 * React Query Hooks fuer Text-Regeln
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTextRules,
  getAllTextRules,
  saveTextRule,
  saveTextRulesBulk,
  deleteTextRule,
} from '@/lib/api/channel-readiness'

export function useTextRules(channelId: string | null) {
  return useQuery({
    queryKey: ['text-rules', channelId],
    queryFn: () => getTextRules(channelId!),
    enabled: !!channelId,
    select: (data) => data.rules,
  })
}

export function useAllTextRules() {
  return useQuery({
    queryKey: ['text-rules', 'all'],
    queryFn: getAllTextRules,
    select: (data) => data.rules,
  })
}

export function useSaveTextRule(channelId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rule: {
      attribute_code: string
      rule_type: string
      rule_config: { value: number | boolean }
      error_message?: Record<string, string>
    }) => saveTextRule(channelId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['text-rules', channelId] })
      queryClient.invalidateQueries({ queryKey: ['text-rules', 'all'] })
    },
  })
}

export function useSaveTextRulesBulk(channelId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rules: Array<{
      attribute_code: string
      rule_type: string
      rule_config: { value: number | boolean }
      error_message?: Record<string, string>
    }>) => saveTextRulesBulk(channelId, rules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['text-rules', channelId] })
      queryClient.invalidateQueries({ queryKey: ['text-rules', 'all'] })
    },
  })
}

export function useDeleteTextRule(channelId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ruleId: string) => deleteTextRule(channelId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['text-rules', channelId] })
      queryClient.invalidateQueries({ queryKey: ['text-rules', 'all'] })
    },
  })
}
